import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Sun,
  Moon,
  UserPlus,
  UserMinus,
  Briefcase,
  Users,
  Building2,
  Flag,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {API_URL} from "../components/config"; // Adjust the import based on your project structure
const HRDashboard = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate();
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState(0);
  const [openPositions, setOpenPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bgColor = isDarkMode ? "bg-gray-900" : "bg-gray-100";
  const textColor = isDarkMode ? "text-gray-100" : "text-gray-900";
  const cardBg = isDarkMode ? "bg-gray-800" : "bg-white";
  const primaryColor = "#002966";
  const accentColor = "#ffc000";

  const fetchEmployees = async () => {
    try {
      setLoading(true);

      // Fetch employees and job titles
      const [employeesResponse, jobTitlesResponse, Departments] =
        await Promise.all([
          fetch(`${API_URL}/users/employees/roles`),
          fetch(`${API_URL}/users/job_titles/user_counts`),
          fetch(`${API_URL}/jobtitle/count/departments`),
        ]);

      const data = await employeesResponse.json();
      const jobTitlesData = await jobTitlesResponse.json();
      const departmentsData = await Departments.json();

      console.log("data", departmentsData);
      // Set employees
      setEmployees(data);

      // Calculate total departments
      const uniqueDepartments = new Set(data.map((emp) => emp.department)); // Adjust based on your employee data structure
      setDepartments(departmentsData.department_count);

      // Count open positions (where user count is zero)
      const zeroUserCountPositions = jobTitlesData.filter(
        (job) => job.user_count === 0
      );
      setOpenPositions(zeroUserCountPositions.length);

      console.log("Fetched Employees:", data);
      setError(null);
    } catch (error) {
      setError("Failed to fetch employees");
      console.error("Error fetching employees:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const navCards = [
    {
      title: "Add Employee",
      description: "Add new employees to the system",
      icon: <UserPlus className="w-10 h-10" style={{ color: accentColor }} />,
      path: "/RegisterEmployee",
    },
    {
      title: "Fire Employee",
      description: "Manage employee terminations",
      icon: <UserMinus className="w-10 h-10" style={{ color: accentColor }} />,
      path: "/TerminateUser",
    },
    {
      title: "Add Position",
      description: "Create new job titles and positions",
      icon: <Briefcase className="w-10 h-10" style={{ color: accentColor }} />,
      path: "/RegisterJobTitles",
    },
  ];

  const stats = [
    { title: "Total Employees", value: employees.length, icon: <Users /> },
    { title: "Open Positions", value: openPositions, icon: <Flag /> },
    { title: "Departments", value: departments, icon: <Building2 /> },
  ];

  const activities = [
    {
      icon: <UserPlus />,
      title: "New Employee Added",
      description: "John Doe - Software Developer",
    },
    {
      icon: <Briefcase />,
      title: "New Position Created",
      description: "Senior UI/UX Designer",
    },
    {
      icon: <UserMinus />,
      title: "Employee Contract Terminated",
      description: "Jane Smith - Marketing Manager",
    },
  ];

  return (
    <div
      className={`min-h-screen ${bgColor} ${textColor} transition-colors duration-300`}
    >
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className={`${cardBg} rounded-2xl p-6 mb-8 shadow-lg flex justify-between items-center`}
          style={{ backgroundColor: primaryColor }}
        >
          <h1 className="text-2xl font-bold text-white">HR Dashboard</h1>
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-opacity-20 hover:bg-white transition-colors"
          >
            {isDarkMode ? (
              <Sun className="w-6 h-6 text-white" />
            ) : (
              <Moon className="w-6 h-6 text-white" />
            )}
          </button>
        </motion.div>

        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {navCards.map((card, index) => (
            <motion.div
              key={card.title}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              className={`${cardBg} p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer`}
              whileHover={{ y: -5 }}
              onClick={() => navigate(card.path)}
            >
              <div className="flex flex-col items-center text-center">
                {card.icon}
                <h3
                  className="text-xl font-semibold mt-4 mb-2"
                  style={{ color: accentColor }}
                >
                  {card.title}
                </h3>
                <p className="text-sm opacity-80">{card.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              className={`${cardBg} p-6 rounded-2xl shadow-lg text-center`}
            >
              <div
                className="flex items-center justify-center mb-3"
                style={{ color: accentColor }}
              >
                {stat.icon}
              </div>
              <h3 className="text-lg font-semibold mb-1">{stat.title}</h3>
              <p className="text-2xl font-bold" style={{ color: accentColor }}>
                {stat.value}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Recent Activities */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className={`${cardBg} rounded-2xl p-6 shadow-lg`}
        >
          <h2
            className="text-xl font-semibold mb-6"
            style={{ color: accentColor }}
          >
            Recent Activities
          </h2>
          <div className="space-y-4">
            {activities.map((activity, index) => (
              <motion.div
                key={index}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4 p-4 rounded-lg hover:bg-opacity-50 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <div
                  className="p-2 rounded-full"
                  style={{ color: accentColor }}
                >
                  {activity.icon}
                </div>
                <div>
                  <h4 className="font-semibold">{activity.title}</h4>
                  <p className="text-sm opacity-80">{activity.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HRDashboard;
